var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
/**
 * "Formuláře" sub-page of the CZNA Authorities Settings panel.
 *
 * TASK-180 introduced the raw-JSON editor (`<ModuleSettingEditor>`).
 * TASK-181 layered the visual editor (`<FormConfigVisualEditor>`) on top
 * with a mode toggle in the Pane footer. The default mode is `visual`
 * per analyst recommendation (R4 doporučení (a) — wrap, don't duplicate);
 * the raw editor remains bit-identical and is reachable via the toggle.
 *
 * Renders a static RecordType selector (the MARC types that have form
 * configs per ADR-0005, incl. OLD_PRINT_CITY added in CZNA-181). Admin gate
 * `hasPerm` for UX only; real authorization is the server's 403.
 *
 * TASK-181 review FIX 1 (raw→visual parse guard): the raw textarea state
 * is lifted into this host so we can read it at toggle-time and run a
 * structural parse guard. On success the parsed FormConfig is handed to
 * the visual editor as a one-shot override; on failure the toggle is
 * blocked and ModeToggle renders a warning under the buttons.
 */
import React, { useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Pane, Paneset, Select, MessageBanner, Button, ConfirmationModal, Dropdown, DropdownMenu, Icon, } from '@folio/stripes/components';
import { useStripes } from '@folio/stripes/core';
import ModuleSettingEditor from './ModuleSettingEditor';
import FormAnalysisView from './FormAnalysisView';
import FormConfigDriftDialog from './FormConfigDriftDialog';
import FormConfigHistoryDialog from './FormConfigHistoryDialog';
import FormConfigTemplateDialog from './FormConfigTemplateDialog';
import FormConfigVisualEditor from './visual-editor/FormConfigVisualEditor';
import ModeToggle from './visual-editor/ModeToggle';
import { parseRawToConfig } from './visual-editor/utils/parseGuard';
import NavPaneLayout from '../components/NavPaneLayout';
import { DEFAULT_FORM_CONFIG_TEMPLATE_ID, formConfigSettingKey, RecordType, } from '../types/api';
import useFormConfigTemplates, { templateDisplayName, } from '../hooks/useFormConfigTemplates';
import useFormConfigDrift from '../hooks/useFormConfigDrift';
import useCreateFormConfig from '../hooks/useCreateFormConfig';
import useDeleteModuleSetting from '../hooks/useDeleteModuleSetting';
import useRenameFormConfigTemplate from '../hooks/useRenameFormConfigTemplate';
import FORM_CONFIG_RECORD_TYPES from './formConfigRecordTypes';
import dropdownTriggerFocus from '../utils/dropdownTriggerFocus';
var FormConfigSettings = function () {
    var _a, _b, _c, _d, _e;
    var intl = useIntl();
    var stripes = useStripes();
    var _f = useState(RecordType.PERSONAL), selectedType = _f[0], setSelectedType = _f[1];
    var _g = useState('visual'), mode = _g[0], setMode = _g[1];
    // When the user switches visual → raw we pass the serialised config so
    // the raw editor opens prefilled with the in-memory state (not the
    // last server payload). This is the round-trip glue for WP-D CP-2.
    var _h = useState(null), rawSeed = _h[0], setRawSeed = _h[1];
    // Lifted raw textarea state (TASK-181 review FIX 1). Owned here so the
    // toggle handler can read the current text and run the parse guard.
    // `null` means "no parent control yet" — ModuleSettingEditor falls back
    // to its self-owned state until we render it with controlled props.
    var _j = useState(''), rawText = _j[0], setRawText = _j[1];
    // One-shot config override handed to the visual editor on raw→visual
    // toggle. Cleared once consumed via `onOverrideConsumed`.
    var _k = useState(null), visualSeed = _k[0], setVisualSeed = _k[1];
    // When the parse guard rejects the raw textarea this carries the human
    // message; ModeToggle renders a warning and disables the visual button.
    var _l = useState(null), parseBlockReason = _l[0], setParseBlockReason = _l[1];
    // Latest visual-editor in-memory serialisation. The visual editor
    // streams this up via onSerialisedChange; the toggle handler reads
    // it on visual→raw so the raw editor opens with the live edits
    // (replaces the old in-footer "Přepnout na raw" callback).
    var lastVisualSerialisedRef = useRef('');
    // CZNA-478: the "Analýza formuláře" action replaces the editor pane rather
    // than opening a modal — the analysis is a ~150-200 row table that gets read
    // and sorted, which a dialog is the wrong container for. Kept as local state
    // instead of a route: it must not become a settings nav entry (a new settings
    // route needs five separate touch points to show up correctly, MEMORY
    // `settings-route-nav-membership-gotcha`), and it is only ever reached from
    // this page's action menu.
    var _m = useState(false), analysisOpen = _m[0], setAnalysisOpen = _m[1];
    var _o = useState(false), actionsOpen = _o[0], setActionsOpen = _o[1];
    // CZNA-456: a type now has N named templates, so "which form am I editing"
    // is two coordinates, not one. `null` = "not chosen yet"; the effect below
    // settles it on `default` once the list arrives.
    var _p = useState(null), selectedTemplateId = _p[0], setSelectedTemplateId = _p[1];
    var _q = useState(null), templateDialog = _q[0], setTemplateDialog = _q[1];
    var _r = useState(false), deleteConfirmOpen = _r[0], setDeleteConfirmOpen = _r[1];
    // CZNA-499: the compare dialog for a packaged default that moved on.
    var _s = useState(false), driftDialogOpen = _s[0], setDriftDialogOpen = _s[1];
    // CZNA-1563: who changed the selected template, when, what each change did,
    // and the way back to an earlier version.
    var _t = useState(false), historyOpen = _t[0], setHistoryOpen = _t[1];
    // Whether the editor on screen (raw or visual — each reports its own) holds
    // edits not yet saved. A restore reseeds that editor, so the history dialog
    // has to warn before it discards them.
    var _u = useState(false), editorDirty = _u[0], setEditorDirty = _u[1];
    var canEdit = stripes.hasPerm('czna-authorities.module-settings.item.put');
    var canAnalyse = stripes.hasPerm('czna-authorities.module-settings.analysis.get');
    var canCreateTemplate = stripes.hasPerm('czna-authorities.module-settings.form-configs.post');
    var canDeleteTemplate = stripes.hasPerm('czna-authorities.module-settings.item.delete');
    var canViewHistory = stripes.hasPerm('czna-authorities.module-settings.history.get');
    var _v = useFormConfigTemplates(selectedType), templates = _v.templates, templatesLoading = _v.isLoading, templatesError = _v.isError;
    // CZNA-499. Module-wide, not per-template: the badge has to be right for the
    // template the admin has NOT selected yet, or they only find out a form is
    // stale by happening to click it — which is how CZNA-405 stayed invisible for
    // a day. One request answers for every form on the screen.
    var _w = useFormConfigDrift(), findDrift = _w.driftFor, driftCountForRecordType = _w.driftCountForRecordType;
    // Settle the selection once the list is known, and re-settle when the type
    // changes or the selected template disappears (deleted here, or in another
    // tab). Deriving it inline instead would mean the editor's `key` changes
    // between renders while a fetch is in flight.
    useEffect(function () {
        var _a;
        if (templates.length === 0)
            return;
        var stillThere = selectedTemplateId !== null
            && templates.some(function (t) { return t.templateId === selectedTemplateId; });
        if (stillThere)
            return;
        var fallback = (_a = templates.find(function (t) { return t.templateId === DEFAULT_FORM_CONFIG_TEMPLATE_ID; })) !== null && _a !== void 0 ? _a : templates[0];
        setSelectedTemplateId(fallback.templateId);
    }, [templates, selectedTemplateId]);
    var selectedTemplate = (_a = templates.find(function (t) { return t.templateId === selectedTemplateId; })) !== null && _a !== void 0 ? _a : null;
    /**
     * Which key the editors read and write. Until the template list resolves we
     * point at `default`, which is the one key guaranteed to exist for a type
     * that has a packaged form — so the editor loads the complete form rather
     * than flickering through a 404.
     */
    var settingKey = formConfigSettingKey(selectedType, selectedTemplateId !== null && selectedTemplateId !== void 0 ? selectedTemplateId : DEFAULT_FORM_CONFIG_TEMPLATE_ID);
    /** CZNA-499 — drift for the form currently on screen, or `null`. */
    var selectedDrift = findDrift(settingKey);
    var createTemplate = useCreateFormConfig();
    var renameTemplate = useRenameFormConfigTemplate(settingKey, {
        recordType: selectedType,
        onSuccess: function () { return setTemplateDialog(null); },
    });
    var deleteTemplate = useDeleteModuleSetting(settingKey, {
        recordType: selectedType,
        // Drop the selection so the settle-effect re-picks `default`; the deleted
        // row is already gone from the invalidated list.
        onSuccess: function () { return setSelectedTemplateId(null); },
    });
    // CZNA-499 Kontrola: mark the record type too, not only its templates. The
    // template picker only ever lists templates of the type already selected, so
    // a stale SUBJECT form was invisible to an admin sitting on the default
    // PERSONAL — you had to click all nine types to find it. That is exactly the
    // discovery-by-accident this ticket was raised to end, one level up.
    var options = FORM_CONFIG_RECORD_TYPES.map(function (type) {
        var name = intl.formatMessage({
            id: "ui-czna-authorities.settings.forms.recordType.".concat(type),
        });
        var driftedTemplates = driftCountForRecordType(type);
        return {
            value: type,
            label: driftedTemplates > 0
                ? intl.formatMessage({ id: 'ui-czna-authorities.settings.forms.drift.recordTypeSuffix' }, { name: name, count: driftedTemplates })
                : name,
        };
    });
    var typeSelectorId = 'settings-editor-type-selector-input';
    // Stripes Select stacks label above the input by default. We want
    // the label and the dropdown on one row so the top toolbar reads as
    // a single header line — render a manual <label> next to the Select
    // and suppress the built-in one (aria-labelledby keeps a11y intact).
    var typeSelector = (React.createElement("div", { "data-testid": "settings-editor-type-selector", style: { display: 'inline-flex', alignItems: 'center', gap: '0.5rem' } },
        React.createElement("label", { htmlFor: typeSelectorId, id: "".concat(typeSelectorId, "-label"), style: { whiteSpace: 'nowrap', fontWeight: 500 } },
            intl.formatMessage({ id: 'ui-czna-authorities.settings.forms.recordType.label' }),
            ":"),
        React.createElement("div", { style: { minWidth: '12rem' } },
            React.createElement(Select, { id: typeSelectorId, "data-testid": "settings-editor-type-select", "aria-labelledby": "".concat(typeSelectorId, "-label"), value: selectedType, dataOptions: options, marginBottom0: true, onChange: function (e) {
                    setSelectedType(e.target.value);
                    // CZNA-456: template ids are scoped to a type, so the current one is
                    // meaningless for the new type. Clearing it lets the settle-effect
                    // re-pick `default` from the incoming list instead of carrying over
                    // an id that may not exist there.
                    setSelectedTemplateId(null);
                    // Reset mode on record-type change so each type starts in visual mode
                    setMode('visual');
                    // The analysis deliberately stays open across a type change: its pane
                    // carries this same selector, so switching type is how a reader
                    // compares one form's coverage against another's. `key` on
                    // FormAnalysisView remounts it, and the query is keyed by type, so
                    // the numbers always belong to the type on screen.
                    setRawSeed(null);
                    setRawText('');
                    setVisualSeed(null);
                    setParseBlockReason(null);
                } }))));
    // CZNA-456 — the second coordinate. Rendered even when a type has a single
    // template: the admin has to be able to SEE that the form on screen is
    // "Kompletní" before copying it, and a control that appears only sometimes
    // makes the copy action look like it came from nowhere.
    var templateSelectorId = 'settings-editor-template-selector-input';
    // CZNA-452: packaged templates ship with no `templateLabel`, so without this
    // the selector read "default (výchozí)" — the id where the name belongs. See
    // `templateDisplayName` for why this is a display fallback and not a label
    // baked into the packaged JSON.
    var packagedDefaultName = intl.formatMessage({
        id: 'ui-czna-authorities.settings.forms.templates.defaultName',
    });
    var templateOptions = templates.map(function (t) {
        var name = t.userCreated
            ? templateDisplayName(t, intl.locale, packagedDefaultName)
            : intl.formatMessage({ id: 'ui-czna-authorities.settings.forms.templates.packagedSuffix' }, { name: templateDisplayName(t, intl.locale, packagedDefaultName) });
        // CZNA-499 — mark a stale form in the picker itself. A banner only helps
        // once the admin has already selected the affected template; the whole
        // point is to be visible before they do.
        return {
            value: t.templateId,
            label: findDrift(t.settingKey)
                ? intl.formatMessage({ id: 'ui-czna-authorities.settings.forms.drift.templateSuffix' }, { name: name })
                : name,
        };
    });
    var templateSelector = (React.createElement("div", { "data-testid": "settings-editor-template-selector", style: { display: 'inline-flex', alignItems: 'center', gap: '0.5rem' } },
        React.createElement("label", { htmlFor: templateSelectorId, id: "".concat(templateSelectorId, "-label"), style: { whiteSpace: 'nowrap', fontWeight: 500 } },
            intl.formatMessage({ id: 'ui-czna-authorities.settings.forms.templates.label' }),
            ":"),
        React.createElement("div", { style: { minWidth: '12rem' } },
            React.createElement(Select, { id: templateSelectorId, "data-testid": "settings-editor-template-select", "aria-labelledby": "".concat(templateSelectorId, "-label"), value: selectedTemplateId !== null && selectedTemplateId !== void 0 ? selectedTemplateId : '', dataOptions: templateOptions, disabled: templatesLoading || templates.length === 0, marginBottom0: true, onChange: function (e) {
                    setSelectedTemplateId(e.target.value);
                    // Same reset the type selector does — a different template is a
                    // different document, so in-memory raw/visual seeds are stale.
                    setMode('visual');
                    setRawSeed(null);
                    setRawText('');
                    setVisualSeed(null);
                    setParseBlockReason(null);
                } }))));
    var modeToggleControl = (React.createElement(ModeToggle, { mode: mode, disabledReason: parseBlockReason, onChange: function (next) {
            if (next === 'visual' && mode === 'raw') {
                // Parse-guard the lifted raw text before letting the visual
                // editor take over. On failure stay in raw mode and surface a
                // warning via ModeToggle; on success hand the parsed config
                // to the visual editor as a one-shot override.
                var result = parseRawToConfig(rawText);
                if (result.ok === false) {
                    setParseBlockReason(result.message);
                    return;
                }
                setVisualSeed(result.config);
                setParseBlockReason(null);
            }
            if (next === 'raw') {
                // Leaving visual → raw: clear any stale visual seed and seed
                // the raw editor with the live in-memory edits the visual
                // editor streamed up via onSerialisedChange. Without this
                // hand-off the raw editor would reload the stale server
                // payload and discard the user's unsaved visual edits.
                setVisualSeed(null);
                var live = lastVisualSerialisedRef.current;
                if (live) {
                    setRawSeed(live);
                    setRawText(live);
                }
            }
            setMode(next);
            if (next === 'visual') {
                // Once we're in visual mode the raw seed/text become stale —
                // the next visual→raw toggle re-serialises from the reducer.
                setRawSeed(null);
            }
        } }));
    // CZNA-478: kebab for actions that are neither editing nor mode-switching.
    // Rendered in the shared top toolbar (which both editors receive as `header`)
    // rather than in each editor's own Pane actionMenu — one place, both modes.
    //
    // CZNA-456 added the three template actions here for the same reason: they
    // apply to whichever form the two selectors currently name, in either mode.
    //
    // CZNA-1563 added "Historie změn" — likewise about the form the selectors name.
    var showActionsMenu = canAnalyse || canCreateTemplate || canDeleteTemplate || canViewHistory;
    var actionsMenu = showActionsMenu ? (React.createElement(Dropdown, { id: "form-config-actions", open: actionsOpen, onToggle: function () { return setActionsOpen(function (prev) { return !prev; }); }, renderTrigger: function (_a) {
            var getTriggerProps = _a.getTriggerProps, triggerRef = _a.triggerRef, onToggle = _a.onToggle, ariaProps = _a.ariaProps, keyHandler = _a.keyHandler;
            return (React.createElement(Button, __assign({ ref: triggerRef, buttonStyle: "default", marginBottom0: true, onClick: onToggle, onKeyDown: keyHandler }, dropdownTriggerFocus(getTriggerProps), { "data-testid": "form-config-actions-trigger" }, ariaProps),
                React.createElement(Icon, { icon: "ellipsis" },
                    React.createElement(FormattedMessage, { id: "ui-czna-authorities.settings.forms.actions.menuLabel" }))));
        } },
        React.createElement(DropdownMenu, { "aria-label": intl.formatMessage({
                id: 'ui-czna-authorities.settings.forms.actions.menuLabel',
            }) },
            React.createElement("div", { role: "menu" },
                canAnalyse && (React.createElement(Button, { buttonStyle: "dropdownItem", role: "menuitem", "data-testid": "form-config-action-analysis", onClick: function () {
                        setActionsOpen(false);
                        setAnalysisOpen(true);
                    } },
                    React.createElement(Icon, { icon: "report" },
                        React.createElement(FormattedMessage, { id: "ui-czna-authorities.settings.forms.actions.analysis" })))),
                canCreateTemplate && (React.createElement(Button, { buttonStyle: "dropdownItem", role: "menuitem", "data-testid": "form-config-action-copy-template", disabled: selectedTemplate === null, onClick: function () {
                        setActionsOpen(false);
                        createTemplate.reset();
                        setTemplateDialog('create');
                    } },
                    React.createElement(Icon, { icon: "duplicate" },
                        React.createElement(FormattedMessage, { id: "ui-czna-authorities.settings.forms.templates.actions.copy" })))),
                canEdit && (React.createElement(Button, { buttonStyle: "dropdownItem", role: "menuitem", "data-testid": "form-config-action-rename-template", disabled: selectedTemplate === null, onClick: function () {
                        setActionsOpen(false);
                        renameTemplate.reset();
                        setTemplateDialog('rename');
                    } },
                    React.createElement(Icon, { icon: "edit" },
                        React.createElement(FormattedMessage, { id: "ui-czna-authorities.settings.forms.templates.actions.rename" })))),
                canViewHistory && (React.createElement(Button, { buttonStyle: "dropdownItem", role: "menuitem", "data-testid": "form-config-action-history", disabled: selectedTemplate === null, onClick: function () {
                        setActionsOpen(false);
                        setHistoryOpen(true);
                    } },
                    React.createElement(Icon, { icon: "clock" },
                        React.createElement(FormattedMessage, { id: "ui-czna-authorities.settings.forms.actions.history" })))),
                canDeleteTemplate && (
                // Only NK's own templates can be deleted. A packaged row's DELETE
                // means "reset to the shipped default", which the editors already
                // offer as an explicit "Obnovit výchozí" button — surfacing the same
                // call here under the word "Smazat" would read as data loss and do
                // something else entirely.
                React.createElement(Button, { buttonStyle: "dropdownItem", role: "menuitem", "data-testid": "form-config-action-delete-template", disabled: selectedTemplate === null || !selectedTemplate.userCreated, onClick: function () {
                        setActionsOpen(false);
                        deleteTemplate.reset();
                        setDeleteConfirmOpen(true);
                    } },
                    React.createElement(Icon, { icon: "trash" },
                        React.createElement(FormattedMessage, { id: "ui-czna-authorities.settings.forms.templates.actions.delete" })))))))) : null;
    // Single top toolbar — record type on the left, mode toggle on the right.
    // Previously the toggle showed at the bottom in visual mode and at the
    // top in raw mode (two places, asymmetric). One row at the top is the
    // less-surprising layout and removes the duplicate render path.
    var topToolbar = (React.createElement("div", { "data-testid": "settings-editor-top-toolbar", style: {
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'space-between',
            gap: '1rem',
            flexWrap: 'wrap',
        } },
        React.createElement("div", { style: { display: 'inline-flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' } },
            typeSelector,
            templateSelector),
        React.createElement("div", { style: { display: 'inline-flex', alignItems: 'flex-end', gap: '0.5rem' } },
            modeToggleControl,
            actionsMenu)));
    var templateDialogs = (React.createElement(React.Fragment, null,
        templateDialog !== null && selectedTemplate !== null && (React.createElement(FormConfigTemplateDialog, { open: true, mode: templateDialog, sourceName: templateDisplayName(selectedTemplate, intl.locale, packagedDefaultName), initialLabelCs: templateDialog === 'rename'
                ? ((_c = (_b = selectedTemplate.templateLabel) === null || _b === void 0 ? void 0 : _b.cs) !== null && _c !== void 0 ? _c : '')
                : '', initialLabelEn: templateDialog === 'rename'
                ? ((_e = (_d = selectedTemplate.templateLabel) === null || _d === void 0 ? void 0 : _d.en) !== null && _e !== void 0 ? _e : '')
                : '', isSubmitting: templateDialog === 'create'
                ? createTemplate.isLoading
                : renameTemplate.isLoading, errorStatus: templateDialog === 'create'
                ? createTemplate.errorStatus
                : renameTemplate.errorStatus, errorBody: templateDialog === 'create'
                ? createTemplate.errorBody
                : renameTemplate.errorBody, timedOut: templateDialog === 'create'
                ? createTemplate.timedOut
                : renameTemplate.timedOut, failed: templateDialog === 'create'
                ? createTemplate.isError
                : renameTemplate.isError, readTimedOut: templateDialog === 'rename' && renameTemplate.readTimedOut, onClose: function () { return setTemplateDialog(null); }, onSubmit: function (values) {
                if (templateDialog === 'rename') {
                    renameTemplate.mutate({ templateLabel: values.templateLabel });
                    return;
                }
                // `copyFrom` + `templateLabel` in one request: the user copies and
                // names in one step, so the backend parses and re-serialises the
                // source rather than persisting it byte-identically. That is the
                // documented consequence of sending both, not a surprise.
                createTemplate.mutateAsync({
                    recordType: selectedType,
                    templateId: values.templateId,
                    templateLabel: values.templateLabel,
                    copyFrom: selectedTemplate.settingKey,
                }).then(function () {
                    setTemplateDialog(null);
                    // Land the admin in the copy they just made — the point of the
                    // action is to edit it, and leaving them on the source is how you
                    // get edits saved into the wrong template.
                    setSelectedTemplateId(values.templateId);
                }).catch(function () {
                    // Handled by the dialog via errorStatus/errorBody; swallowing the
                    // rejection here only stops an unhandled-rejection warning.
                });
            } })),
        React.createElement(ConfirmationModal, { open: deleteConfirmOpen, heading: intl.formatMessage({
                id: 'ui-czna-authorities.settings.forms.templates.confirmDelete.heading',
            }), message: intl.formatMessage({ id: 'ui-czna-authorities.settings.forms.templates.confirmDelete.message' }, {
                name: selectedTemplate
                    ? templateDisplayName(selectedTemplate, intl.locale, packagedDefaultName)
                    : '',
            }), confirmLabel: intl.formatMessage({
                id: 'ui-czna-authorities.settings.forms.templates.confirmDelete.confirm',
            }), cancelLabel: intl.formatMessage({
                id: 'ui-czna-authorities.settings.forms.templates.confirmDelete.cancel',
            }), buttonStyle: "danger", isConfirmButtonDisabled: deleteTemplate.isLoading, onConfirm: function () {
                setDeleteConfirmOpen(false);
                deleteTemplate.mutate();
            }, onCancel: function () { return setDeleteConfirmOpen(false); } })));
    /**
     * Failures of the template layer itself. Kept above the editor rather than
     * inside the dialogs because they are about the page's state, not about a
     * form the user is filling in: a 409 on delete means the backend refused to
     * remove the last template of a type.
     */
    /**
     * CZNA-499 — the persistent state the ticket asks for (§2). Not a modal fired
     * by the deploy: the deploy runs unattended through AWX, where a modal would
     * either hang it or be seen by nobody. A banner that stays until somebody
     * decides is the form of notice that survives an unattended upgrade.
     */
    var driftBanner = selectedDrift !== null ? (React.createElement(MessageBanner, { type: "warning", "data-testid": "settings-editor-drift-banner" },
        React.createElement(FormattedMessage, { id: "ui-czna-authorities.settings.forms.drift.banner", values: { count: selectedDrift.incomingChangeCount } }),
        ' ',
        React.createElement(Button, { buttonStyle: "primary", marginBottom0: true, "data-testid": "settings-editor-drift-compare", onClick: function () { return setDriftDialogOpen(true); } },
            React.createElement(FormattedMessage, { id: "ui-czna-authorities.settings.forms.drift.compare" })))) : null;
    var templateBanners = (React.createElement(React.Fragment, null,
        driftBanner,
        templatesError && (React.createElement(MessageBanner, { type: "warning", "data-testid": "settings-editor-templates-error" },
            React.createElement(FormattedMessage, { id: "ui-czna-authorities.settings.forms.templates.listError" }))),
        deleteTemplate.errorStatus === 409 && (React.createElement(MessageBanner, { type: "error", "data-testid": "settings-editor-delete-conflict" },
            React.createElement(FormattedMessage, { id: "ui-czna-authorities.settings.forms.templates.error.lastTemplate" }))),
        deleteTemplate.timedOut && (React.createElement(MessageBanner, { type: "error", "data-testid": "settings-editor-delete-timeout" },
            React.createElement(FormattedMessage, { id: "ui-czna-authorities.error.timeout" }))),
        !deleteTemplate.timedOut
            && deleteTemplate.errorStatus !== null && deleteTemplate.errorStatus !== 409 && (React.createElement(MessageBanner, { type: "error", "data-testid": "settings-editor-delete-error" },
            React.createElement(FormattedMessage, { id: "ui-czna-authorities.settings.forms.templates.error.deleteFailed", values: { status: deleteTemplate.errorStatus } })))));
    if (canEdit === false) {
        return (React.createElement(Paneset, null,
            React.createElement(Pane, { id: "settings-nav-pane", defaultWidth: "20%", paneTitle: React.createElement(FormattedMessage, { id: "ui-czna-authorities.pane.navigation" }) },
                React.createElement(NavPaneLayout, null)),
            React.createElement(Pane, { defaultWidth: "fill", fluidContentWidth: true, paneTitle: React.createElement(FormattedMessage, { id: "ui-czna-authorities.settings.forms.paneTitle" }) },
                React.createElement("div", { "data-testid": "settings-editor-admin-only-placeholder" },
                    React.createElement(MessageBanner, { type: "warning" },
                        React.createElement(FormattedMessage, { id: "ui-czna-authorities.settings.forms.placeholder.adminOnly" }))))));
    }
    // The editors render whatever `header` they are handed at the top of their
    // own pane, so the template banners ride along with the toolbar instead of
    // needing a pane of their own.
    var editorHeader = (React.createElement(React.Fragment, null,
        topToolbar,
        templateBanners));
    return (React.createElement(React.Fragment, null,
        React.createElement(Paneset, null,
            React.createElement(Pane, { id: "settings-nav-pane", defaultWidth: "20%", paneTitle: React.createElement(FormattedMessage, { id: "ui-czna-authorities.pane.navigation" }) },
                React.createElement(NavPaneLayout, null)),
            analysisOpen ? (React.createElement(FormAnalysisView, { key: "analysis-".concat(selectedType), type: selectedType, onClose: function () { return setAnalysisOpen(false); }, header: typeSelector })) : mode === 'visual' ? (React.createElement(FormConfigVisualEditor, { key: "visual-".concat(settingKey), settingKey: settingKey, header: editorHeader, initialConfigOverride: visualSeed, onOverrideConsumed: function () { return setVisualSeed(null); }, onSerialisedChange: function (s) { lastVisualSerialisedRef.current = s; }, onDirtyChange: setEditorDirty })) : (React.createElement(ModuleSettingEditor, { key: "raw-".concat(settingKey), settingKey: settingKey, header: editorHeader, seedTextOverride: rawSeed, value: rawText, onDirtyChange: setEditorDirty, onChange: function (next) {
                    setRawText(next);
                    // Any keystroke invalidates the previous parse-block warning
                    // (user is actively editing; let them try the toggle again).
                    if (parseBlockReason !== null)
                        setParseBlockReason(null);
                } }))),
        templateDialogs,
        driftDialogOpen && (React.createElement(FormConfigDriftDialog, { open: true, settingKey: settingKey, recordType: selectedType, formName: selectedTemplate
                ? templateDisplayName(selectedTemplate, intl.locale, packagedDefaultName)
                : settingKey, onClose: function () { return setDriftDialogOpen(false); } })),
        historyOpen && (React.createElement(FormConfigHistoryDialog, { open: true, settingKey: settingKey, formName: selectedTemplate
                ? templateDisplayName(selectedTemplate, intl.locale, packagedDefaultName)
                : settingKey, canRestore: canEdit, 
            // The analysis replaces the editor, and with it any unsaved edits.
            hasUnsavedEdits: editorDirty && !analysisOpen, onClose: function () { return setHistoryOpen(false); } }))));
};
export default FormConfigSettings;
